import { useState } from "react";
import { Link } from "react-router-dom";
import { useIsFetching, useMutation, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { Td } from "../../features/web3/forex/components/table";
import { Button } from "../../components/ui/button";
import { formatDateTime } from "../../lib/format";
import { adminPost, useCan, useForexAdmin } from "./api";
import {
  BASE,
  Drawer,
  ListView,
  Panel,
  PausedBy,
  QueryState,
  Stat,
  inputCls,
  labelCls,
  linkCls,
  mono,
  muted,
  orDash,
} from "./parts";

// 022: Ginox's own copy engine. Status polls every 10s and the problems every 30s,
// as the handoff suggests, inside the 100 requests a minute operator budget.

// The handoff's suggested labels. CLOSE_FAILING is still being retried, so it can't
// be resolved (409).
const PROBLEM_LABEL = {
  UNKNOWN: "Open not confirmed",
  CLOSE_FAILING: "Close failing",
  MASTER_VANISHED: "Master position vanished",
  MASTER_RENEWED: "Master position reissued",
  COPY_VANISHED: "Copy vanished",
  COPY_RENEWED: "Copy reissued",
  CLOSE_UNKNOWN: "Close not confirmed",
  CLOSED_OUTSIDE: "Closed outside",
  ORPHAN: "Orphan position",
};

const errBox = "rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300";
const ms = (v) => (v == null ? "—" : `${v} ms`);

function Status() {
  const canPause = useCan()("copy_engine.pause");
  const qc = useQueryClient();
  const q = useForexAdmin("/copy-engine/admin/status", undefined, { refetchInterval: 10_000 });
  const [confirming, setConfirming] = useState(false);
  const toggle = useMutation({
    mutationFn: (paused) => adminPost(`/copy-engine/admin/${paused ? "resume" : "pause"}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["forex-admin"] });
      setConfirming(false);
    },
  });

  return (
    <QueryState q={q}>
      {(s) => (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Engine" tone={s.running ? undefined : "text-red-400"}>{s.running ? "Running" : "Not running"}</Stat>
            <Stat label="Copying" tone={s.paused ? "text-amber-400" : undefined}>{s.paused ? "Paused" : "On"}</Stat>
            <Stat label="Last progress"><span className="text-xs">{formatDateTime(s.lastProgressAt)}</span></Stat>
            <Stat label="Last copy"><span className="text-xs">{formatDateTime(s.lastCopyAt)}</span></Stat>
            <Stat label="Last event"><span className="text-xs">{formatDateTime(s.lastEventAt)}</span></Stat>
            <Stat label="Last reconcile"><span className="text-xs">{formatDateTime(s.lastReconciledAt)}</span></Stat>
            <Stat label="Reconcile lag">{ms(s.reconcileLagMs)}</Stat>
            <Stat label="Lease">
              <span className="text-xs">{orDash(s.holder)} · epoch {orDash(s.epoch)} · until {formatDateTime(s.leaseExpiresAt)}</span>
            </Stat>
            <Stat label="Copies timed">{s.delay.copies}</Stat>
            <Stat label="Delay median">{ms(s.delay.medianMs)}</Stat>
            <Stat label="Delay p95">{ms(s.delay.p95Ms)}</Stat>
            <Stat label="Delay max">{ms(s.delay.maxMs)}</Stat>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {canPause &&
              (s.paused ? (
                <Button size="sm" disabled={toggle.isPending} onClick={() => toggle.mutate(true)}>Resume all copying</Button>
              ) : (
                <Button size="sm" variant="destructive" onClick={() => setConfirming(true)}>Pause all copying</Button>
              ))}
            {s.paused && (
              <span className="text-xs text-amber-400">
                Paused {formatDateTime(s.pausedAt)} by <PausedBy id={s.pausedBy} />. Closes still go on.
              </span>
            )}
          </div>
          {toggle.error && !confirming && <p className={errBox}>{toggle.error.message}</p>}

          <Drawer open={confirming} onClose={() => { setConfirming(false); toggle.reset(); }} title="Pause all copying">
            <p className="text-sm text-muted-foreground">
              New copies stop for every copier at once. Open copies still close with their masters. A master trade
              opened while paused is skipped and is never copied later, even after you resume.
            </p>
            {toggle.error && <p className={errBox}>{toggle.error.message}</p>}
            <Button size="sm" variant="destructive" disabled={toggle.isPending} onClick={() => toggle.mutate(false)}>
              {toggle.isPending ? "Pausing…" : "Pause all copying"}
            </Button>
          </Drawer>
        </div>
      )}
    </QueryState>
  );
}

const PROBLEM_COLUMNS = [
  { label: "Since" },
  { label: "Problem" },
  { label: "Kind" },
  { label: "State" },
  { label: "Symbol" },
  { label: "Side" },
  { label: "Volume", align: "right" },
  { label: "Copier login · position" },
  { label: "Master login · position" },
  { label: "Close failures", align: "right" },
  { label: "Connection" },
  { label: "" },
];

// onResolve is null without copy_engine.resolve (025): no Resolve link then.
const problemRow = (onResolve) => (r) => (
  <tr key={r.id} className="hover:bg-white/[0.02]">
    <Td className={muted}>{formatDateTime(r.since)}</Td>
    <Td className="text-xs font-medium text-amber-400">{PROBLEM_LABEL[r.problem] ?? r.problem}</Td>
    <Td className="text-xs">{r.kind === "ORPHAN" ? "Orphan" : "Copied trade"}</Td>
    <Td className="text-xs">{orDash(r.state)}</Td>
    <Td className={mono}>{r.symbol}</Td>
    <Td className="text-xs">{r.side}</Td>
    <Td align="right" className={mono}>{r.volume}</Td>
    <Td className={mono}>{r.copierLogin} · {orDash(r.copierPosition)}</Td>
    <Td className={mono}>{orDash(r.masterLogin)} · {orDash(r.masterPosition)}</Td>
    <Td align="right" className={mono}>{r.closeFailures}</Td>
    <Td className="text-xs">
      {r.connectionId ? <Link to={`${BASE}/connections/${r.connectionId}`} className={linkCls}>Open →</Link> : "—"}
    </Td>
    <Td className="text-xs">
      {onResolve && r.problem !== "CLOSE_FAILING" && (
        <button type="button" onClick={() => onResolve(r)} className={linkCls}>Resolve</button>
      )}
    </Td>
  </tr>
);

function ResolveDrawer({ problem, onClose }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  // A declaration, so the mutation's callbacks below can call it.
  function close() {
    setNote("");
    m.reset();
    onClose();
  }
  const m = useMutation({
    mutationFn: () => adminPost(`/copy-engine/admin/problems/${problem.id}/resolve`, { note: note.trim() }),
    // The drawer stays open on the answer (022: show it); the list refetches behind it.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["forex-admin"] }),
    // A retry answers "already resolved": it went through.
    onError: (err) => {
      if (err.status === 409 && /already resolved/i.test(err.message)) {
        qc.invalidateQueries({ queryKey: ["forex-admin"] });
        close();
      }
    },
  });
  const valid = note.trim().length >= 1 && note.trim().length <= 1000;

  return (
    <Drawer open={!!problem} onClose={close} title="Resolve problem">
      {problem && (
        <p className="text-sm text-muted-foreground">
          {PROBLEM_LABEL[problem.problem] ?? problem.problem} · {problem.symbol} {problem.side} {problem.volume} on login{" "}
          {problem.copierLogin}. Resolving drops it from this list. It does not close or change any position.
        </p>
      )}
      {m.data ? (
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Resolved"><span className="text-sm text-emerald-400">{PROBLEM_LABEL[m.data.problem] ?? m.data.problem}</span></Stat>
          <Stat label="Kind"><span className="text-sm">{m.data.kind}</span></Stat>
          <Stat label="Id"><span className="break-all text-xs">{m.data.id}</span></Stat>
          {/* null on an orphan; SKIPPED for a resolved UNKNOWN. */}
          <Stat label="Trade state now"><span className="text-sm">{orDash(m.data.state)}</span></Stat>
          <Stat label="At"><span className="text-xs">{formatDateTime(m.data.resolvedAt)}</span></Stat>
          <Stat label="By"><span className="text-sm"><PausedBy id={m.data.resolvedBy} /></span></Stat>
          <Stat label="Note"><span className="break-words text-sm">{m.data.note}</span></Stat>
        </div>
      ) : (
        <>
          <div>
            <label className={labelCls}>Note (required, up to 1000 characters)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={1000} className={inputCls} />
          </div>
          {m.error && <p className={errBox}>{m.error.message}</p>}
          <Button size="sm" disabled={!valid || m.isPending} onClick={() => m.mutate()}>
            {m.isPending ? "Resolving…" : "Resolve"}
          </Button>
        </>
      )}
    </Drawer>
  );
}

export default function CopyEngine() {
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;
  const [resolving, setResolving] = useState(null);
  const canResolve = useCan()("copy_engine.resolve");

  return (
    <QueueLayout
      eyebrow="Forex Admin"
      title="Copy"
      accent="engine"
      description="Whether the engine runs, how fast copies land, and every copied trade in trouble."
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <div className="space-y-10">
        <Panel title="Status">
          <Status />
        </Panel>
        <Panel title="Problems">
          <ListView
            path="/copy-engine/admin/problems"
            empty={{}}
            fields={[]}
            columns={PROBLEM_COLUMNS}
            row={problemRow(canResolve ? setResolving : null)}
            label="problems"
            emptyText="No copied trade is in trouble."
            queryOpts={{ refetchInterval: 30_000 }}
          />
        </Panel>
      </div>
      <ResolveDrawer problem={resolving} onClose={() => setResolving(null)} />
    </QueueLayout>
  );
}
